import { useEffect, useState } from 'react';
import type { ProfileRecord } from '@youscience/bpa-common';
import { CoreDialogContent, CoreTypography } from '@youscience/core';
import CoreDatePicker from '@components/CoreDatePicker';

import DialogFooter from '@components/DialogFooter';
import { GradeAutocomplete } from '@components/GatherPersonalInfo/GradeAutocomplete';

import { ATTENDING_YEAR, CURRENT_YEAR } from '@constants';
import { ATTENDING_YEAR_BY_GRADE } from '@constants/educationPlanner';

import useAuthStore, { ProfileGradeAndPlan, ProfileWithExtendedData } from '@store/AuthStore';
import useUserStore from '@store/UserStore';
import useEducationPlannerStore from '@store/EducationPlannerStore';
import useNotificationStore from '@store/NotificationStore';

import usePlannerQuestionerStore from '@store/PlannerQuestionerStore';

import { getGraduationYear } from '@utils/studentProgress';

import { SummaryContent } from './SummaryContent';
import { AttendingCollegeAutocomplete } from './AttendingCollegeAutocomplete';

import { sxStyles, StyledDialog, StyledDialogFormControls, StyledDialogTitle } from './GatherPersonalInfo.styles';

interface GatherPersonalInfoProps {
  isOpenGatherPersonalInfo: boolean;
  componentId?: string;
  summaryTitle?: string;
  saveTitle?: string;
  cancelTitle?: string;

  onSave?: (profileData: ProfileData) => void;
  onCancel?: () => void;
}

export interface ProfileData extends ProfileGradeAndPlan {
  beginCollegeYear?: number;
  beginCollegeSeason?: string;
  beginCollegeOther?: string;
  graduationYear?: number | null;
  dateOfBirth?: Date | null;
  plan?: ProfileRecord['plan'];
}

const mask = '__/__/____';

export const GatherPersonalInfo = (props: GatherPersonalInfoProps) => {
  const { isOpenGatherPersonalInfo, onSave, onCancel, saveTitle, cancelTitle, summaryTitle } = props;

  const { userData, userCard } = useAuthStore((state) => state.authSession);
  const {
    userPlanning: { beginCollege: initialBeginCollege },
    setEducationPlannerData,
  } = useEducationPlannerStore((state) => state);
  const { savePlannerQuestionerData } = usePlannerQuestionerStore((state) => state);

  const { updateUser } = useUserStore((state) => state);

  const { dateOfBirth: initialDateOfBirth, graduationYear: reconciledGraduationYear } = (userData?.profile ??
    {}) as ProfileWithExtendedData;

  const initialGraduationYear = (userCard ? userCard.graduationYear : reconciledGraduationYear) ?? undefined;

  const [datePickerInError, setDatePickerInError] = useState(false);
  const [attendingYear, setAttendingYear] = useState(ATTENDING_YEAR);
  const [requireMoreInfo, setRequireMoreInfo] = useState(true);
  const [dataIsSaving, setDataIsSaving] = useState(false);

  const initialGradeLevel = (userData?.profile as ProfileWithExtendedData)?.gradeLevel;
  const initialPlan = (userData?.profile as ProfileWithExtendedData)?.plan;

  const [newProfileData, setNewProfileData] = useState<ProfileData>({
    beginCollegeYear: initialBeginCollege?.year,
    beginCollegeSeason: initialBeginCollege?.season,
    beginCollegeOther: initialBeginCollege?.other,
    plan: initialPlan,
    dateOfBirth: initialDateOfBirth ? new Date(initialDateOfBirth) : null,
    gradeLevel: initialGradeLevel,
    graduationYear: initialGraduationYear,
  });

  const { beginCollegeYear, beginCollegeSeason, beginCollegeOther, plan, dateOfBirth, gradeLevel, graduationYear } =
    newProfileData;

  const [popperOpen, setPopperOpen] = useState(false);

  const handleCancelButtonClicked = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleSaveButtonClicked = async () => {
    if (requireMoreInfo) {
      return;
    }

    setDataIsSaving(true);

    try {
      const updates = [
        updateUser({
          profile: {
            dateOfBirth: dateOfBirth?.toISOString(),
            graduationYear,
            plan,
          },
        }),
      ];

      if ((beginCollegeYear && beginCollegeSeason) || beginCollegeOther) {
        updates.push(
          savePlannerQuestionerData({
            beginCollege: {
              year: beginCollegeYear,
              season: beginCollegeSeason,
              other: beginCollegeOther,
            },
          }),
        );
      }

      await Promise.all(updates);
    } catch (err) {
      useNotificationStore.getState().notify({
        message: 'There was an error saving your data. Please try again later.',
        severity: 'error',
      });
    }

    setEducationPlannerData({
      currentGrade: gradeLevel,
    });

    if (onSave) {
      onSave(newProfileData);
    }

    setDataIsSaving(false);
  };

  useEffect(() => {
    const requireMoreInfo =
      !dateOfBirth || (!gradeLevel && !plan?.now) || !(beginCollegeYear || beginCollegeSeason || beginCollegeOther);

    setRequireMoreInfo(requireMoreInfo || datePickerInError);
  }, [dateOfBirth, gradeLevel, beginCollegeYear, beginCollegeSeason, beginCollegeOther, plan, datePickerInError]);

  const handleDateChange = (value: Date | null) => {
    setNewProfileData((prevState) => ({
      ...prevState,
      dateOfBirth: value,
    }));
  };

  const handleAttendingCollegeChange = (value: string | undefined) => {
    let beginCollegeYear: number | undefined;
    let beginCollegeSeason: string | undefined;
    let beginCollegeOther: string | undefined;

    if (value === 'inOverAYear' || value === 'notSure') {
      beginCollegeOther = value;
    } else if (value?.includes(', ')) {
      const [season, year] = value.split(', ');

      beginCollegeYear = Number(year);
      beginCollegeSeason = season;
    }

    setNewProfileData((prevState) => ({
      ...prevState,
      beginCollegeYear,
      beginCollegeSeason,
      beginCollegeOther,
    }));
  };

  const handleGradeChange = (gradeLevel: string | undefined) => {
    if (!gradeLevel) {
      setNewProfileData((prevState) => ({
        ...prevState,
        plan: undefined,
        gradeLevel: undefined,
        graduationYear,
      }));
    } else if (!isNaN(Number(gradeLevel))) {
      const grade = Number(gradeLevel);

      setNewProfileData((prevState) => ({
        ...prevState,
        gradeLevel: grade,
        graduationYear: getGraduationYear(grade),
      }));
    } else {
      setNewProfileData((prevState) => ({
        ...prevState,
        plan: { now: gradeLevel },
        gradeLevel: undefined,
      }));
    }
  };

  useEffect(() => {
    let attendingYear = ATTENDING_YEAR;

    if (gradeLevel === -1) {
      attendingYear = CURRENT_YEAR;
    } else if (gradeLevel && gradeLevel > 0) {
      attendingYear = ATTENDING_YEAR_BY_GRADE[gradeLevel];
    }

    setAttendingYear(attendingYear);
  }, [gradeLevel]);

  return (
    <StyledDialog data-testid="gatherPersonalInfoDialog" open={isOpenGatherPersonalInfo}>
      <StyledDialogTitle data-testid="plannerQuestionerTitle">
        {dataIsSaving ? (
          'Thank you for answering'
        ) : (
          <>
            Let us get to know you better
            <CoreTypography data-testid="subtitle" style={{ marginTop: '1rem' }}>
              We would like to know more about you so we can suggest custom options for your future education.
            </CoreTypography>
          </>
        )}
      </StyledDialogTitle>

      <CoreDialogContent sx={sxStyles.content}>
        {dataIsSaving ? (
          <SummaryContent profileData={newProfileData} summaryTitle={summaryTitle} />
        ) : (
          <StyledDialogFormControls>
            {!initialDateOfBirth ? (
              <CoreDatePicker
                views={['year', 'month', 'day']}
                maxDate={new Date(new Date().setFullYear(CURRENT_YEAR - 10))}
                minDate={new Date(new Date().setFullYear(CURRENT_YEAR - 80))}
                label="Date of Birth"
                mask={mask}
                onChange={handleDateChange}
                onClose={() => setPopperOpen(false)}
                onOpen={() => setPopperOpen(true)}
                PopperProps={{
                  open: popperOpen,
                  placement: 'bottom-start',
                }}
                value={dateOfBirth}
                slotProps={{
                  textField: {
                    sx: sxStyles.datePicker,
                  },
                }}
                onError={(error) => setDatePickerInError(error !== null)}
              />
            ) : null}

            {!initialGradeLevel && !initialPlan?.now ? (
              <GradeAutocomplete handleGradeChange={handleGradeChange} />
            ) : null}

            {!initialBeginCollege?.year && !initialBeginCollege?.season && !initialBeginCollege?.other ? (
              <AttendingCollegeAutocomplete
                handleAttendingCollegeChange={handleAttendingCollegeChange}
                attendingYear={attendingYear}
              />
            ) : null}
          </StyledDialogFormControls>
        )}
      </CoreDialogContent>

      <DialogFooter
        goBackButtonTitle={cancelTitle}
        goNextButtonTitle={saveTitle}
        isDisableNextButton={requireMoreInfo || dataIsSaving}
        onGoBack={handleCancelButtonClicked}
        onGoNext={handleSaveButtonClicked}
      />
    </StyledDialog>
  );
};
